// ============================================================================
// MissingInvalidData Component (View)
// ============================================================================

import { Box, Typography, Alert } from "@mui/material";
import { SkipNext } from "@mui/icons-material";
import { ConfirmDeleteDialog } from "../../components/ConfirmDeleteDialog";
import { useWizardContext } from "../../WizardContext";
import type { MissingInvalidDataProps } from "./MissingInvalidData.types";
import { useMissingInvalidDataViewModel } from "./MissingInvalidData.vm";
import { JobEditForm } from "./JobEditForm";
import "./MissingInvalidData.css";

/**
 * Component for displaying and resolving missing/invalid data issues
 */
export function MissingInvalidData(props: MissingInvalidDataProps) {
	const { onUpdateJob } = props;
	const { formContext } = useWizardContext();
	const vm = useMissingInvalidDataViewModel(props);

	// No issues state
	if (!vm.hasIssues) {
		return (
			<Box data-testid="no-issues-message" className="missing-invalid-data no-issues">
				<Alert severity="success">
					<Typography>No missing or invalid data detected. You can proceed to the next step.</Typography>
				</Alert>
			</Box>
		);
	}

	// No current issue (shouldn't happen but handle gracefully)
	if (!vm.currentIssue || !vm.job) {
		return null;
	}

	return (
		<Box className="missing-invalid-data">
			{/* Issue Description */}
			<Box className="issue-description">
				<Typography variant="h6" gutterBottom>
					Missing or Invalid Data
				</Typography>
				<Typography variant="body2" color="text.secondary">
					{vm.isSkipped
						? "This job has been skipped."
						: `The following job has ${vm.errorFieldCount} validation ${vm.errorFieldCount === 1 ? "error" : "errors"} that need to be fixed. Click "Save Changes" when done.`}
				</Typography>
			</Box>

			{/* Skipped Indicator */}
			{vm.isSkipped && (
				<Box data-testid="skipped-indicator" className="skipped-indicator">
					<SkipNext color="action" />
					<Typography color="text.secondary">This issue has been skipped</Typography>
				</Box>
			)}

			{/* Job Edit Form */}
			<JobEditForm
				job={vm.job}
				formContext={formContext}
				onUpdateJob={onUpdateJob}
				onSaveAndValidate={vm.handleSaveAndValidate}
				onRegisterSave={vm.handleRegisterSave}
				disabled={vm.isSkipped}
			/>

			{/* Delete Confirmation Dialog */}
			<ConfirmDeleteDialog
				open={vm.showDeleteDialog}
				title="Delete Job"
				message={`Are you sure you want to delete "${vm.getJobDisplayName()}"? This action cannot be undone.`}
				onConfirm={vm.handleConfirmDelete}
				onCancel={vm.handleCancelDelete}
			/>
		</Box>
	);
}
